import { AutomationFeature, AutomationsProps } from "@/shared/data/home.v2.data";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import AutomationAccordionItem from "./shared/AutomationAccordionItem";
import SectionHeader from "./shared/SectionHeader";

/**
 * AccordionWithMediaSection — centered header + two-column accordion/media
 * block. This is the reusable primitive behind HomeV2's AutomationsSection
 * and ProductV2's "Create"/"Sell" panels. Supports image-left or image-right
 * orientation via `imagePosition`.
 *
 * Behavior mirrors the original AutomationsSection: auto-advances the
 * active accordion row every 5s, animates a progress bar under each row
 * while it's active, and swaps the right-column image based on the active
 * row. The image slot has fixed geometry so the layout doesn't shift
 * between features.
 */

const AUTO_ADVANCE_MS = 5000;

const FeatureImageSlot = ({
  image,
  imageMobile,
  alt,
}: {
  image?: string;
  imageMobile?: string;
  alt: string;
}) => (
  // Fixed-height media frame so the image never grows with the active
  // accordion item's text length (the grid is `items-center`, so a taller
  // text column just centers this frame rather than stretching it). The
  // inner child fills via `items-stretch`; the image uses object-cover.
  <div className="relative mx-auto flex h-[340px] w-full items-stretch overflow-hidden rounded-[32px] bg-gradient-to-br from-[#FFE0EE] via-[#FFC9DE] to-[#FF9FC7] shadow-[0_24px_60px_-24px_rgba(35,34,40,0.18)] md:h-[520px]">
    <div className="relative w-full">
      {image ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={image}
          alt={alt}
          className={
            imageMobile
              ? "absolute inset-0 hidden h-full w-full object-cover md:block"
              : "absolute inset-0 h-full w-full object-cover"
          }
        />
      ) : null}
      {imageMobile ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={imageMobile}
          alt={alt}
          className={
            image
              ? "absolute inset-0 block h-full w-full object-cover md:hidden"
              : "absolute inset-0 h-full w-full object-cover"
          }
        />
      ) : null}
      {!image && !imageMobile ? (
        <div className="absolute inset-6 rounded-2xl bg-white/40 backdrop-blur-sm" />
      ) : null}
    </div>
  </div>
);

const AccordionWithMediaSection = ({
  eyebrow,
  title,
  subtitle,
  features,
  imagePosition = "right",
  ctas,
}: AutomationsProps) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [progressKey, setProgressKey] = useState(0);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!features.length) return;
    timerRef.current = setTimeout(() => {
      setActiveIdx((i) => (i + 1) % features.length);
      setProgressKey((k) => k + 1);
    }, AUTO_ADVANCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [activeIdx, features.length]);

  const handleToggle = (id: string) => {
    const nextIdx = features.findIndex((f) => f.id === id);
    if (nextIdx === -1) return;
    setActiveIdx(nextIdx);
    setProgressKey((k) => k + 1);
  };

  const activeFeature: AutomationFeature | undefined = features[activeIdx];

  const accordion = (
    <div>
      {features.map((feature, idx) => (
        <AutomationAccordionItem
          key={feature.id}
          id={feature.id}
          title={feature.title}
          description={feature.description}
          bullets={feature.bullets}
          bulletsHeading={feature.bulletsHeading}
          footerLine={feature.footerLine}
          isOpen={idx === activeIdx}
          onToggle={handleToggle}
          isProgressActive={idx === activeIdx}
          progressKey={progressKey}
          showDivider
        />
      ))}
    </div>
  );

  const media = (
    <FeatureImageSlot
      image={activeFeature?.image}
      imageMobile={activeFeature?.imageMobile}
      alt={activeFeature?.title ?? ""}
    />
  );

  return (
    <section className="w-full bg-white">
      <div className="container mx-auto px-4 pt-4 pb-10 sm:px-6 md:px-8 md:pt-6 md:pb-20 lg:px-12 lg:pt-8 lg:pb-24">
        {title && (
          <SectionHeader
            eyebrow={eyebrow}
            titleMuted={title.includes("\n") ? title.split("\n")[0] : undefined}
            titleBright={title.includes("\n") ? title.split("\n")[1] : undefined}
            title={title.includes("\n") ? undefined : title}
            subtitle={subtitle}
            variant="light-teal"
            align="center"
            ctas={ctas}
          />
        )}

        <div
          className={classNames(
            // Asymmetric columns: image column is ~13fr (65%), text column
            // is ~7fr (35%). Flipped based on `imagePosition` so the image
            // always reads wider than the accordion regardless of side.
            "mx-auto mt-12 grid max-w-6xl gap-10 md:mt-16 md:items-center md:gap-16",
            imagePosition === "left"
              ? "md:grid-cols-[13fr_7fr]"
              : "md:grid-cols-[7fr_13fr]"
          )}
        >
          {imagePosition === "left" ? (
            <>
              {media}
              {accordion}
            </>
          ) : (
            <>
              {accordion}
              {media}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default AccordionWithMediaSection;
